import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useSpring, type MotionValue } from 'framer-motion';

import { cn } from 'src/lib/utils';

import type { TourStep } from './types';
import { getTourState } from './tour-store';
import { TourStepCard } from './tour-step-card';
import {
  SPOTLIGHT_PADDING,
  clampToViewport,
  composeHolePath,
  computeCardPlacement,
  isHugeTarget,
  padRect,
  type Rect,
  type Side,
} from './tour-placement';

// Motion language (D6): smooth, sub-400ms, no bounce.
const HOLE_SPRING = { stiffness: 380, damping: 40 };
const CARD_POS_SPRING = { stiffness: 320, damping: 34 };
const HEIGHT_SPRING = { stiffness: 480, damping: 44 };

// 1px top + 1px bottom of the size wrapper's `border`, which sits outside the
// measured (padding-inclusive) card content under border-box sizing.
const CARD_BORDER_Y = 2;

// Card widths per mode (w-[26rem] / w-80). Placement math always uses these
// settled values — never the live offsetWidth — so the 200ms CSS width
// transition can't flicker `fits()` across a boundary mid-animation.
const LARGE_CARD_W = 416;
const COMPACT_CARD_W = 320;

// The card commits to a presentation (centered-large vs anchored-compact) only
// after the target's size has held still this many frames (± tolerance px) AND
// no loading skeleton pulses inside it — and the committed mode then LOCKS for
// the step's remainder. A target mid-data-load grows through the huge
// threshold across several render passes (skeleton → partial → full content)
// and would otherwise flip the card's mode and re-aim it mid-glide — the
// "jumping around" defect on cross-route transitions. ~400 ms of quiet
// outlasts the gaps between those passes. The hole is exempt: it tracks the
// growing target immediately, which reads as a natural morph. Note: quiet
// means no SIZE change — opacity animations (skeleton pulse) don't reset it.
const STABLE_FRAMES = 24;
const SIZE_TOLERANCE = 2;
const VIEWPORT_MARGIN = 16;

// Cross-route continuity (D8): AppLayout — and with it this overlay — unmounts and
// remounts on cross-route navigation. The rAF loop writes the card's current
// position here every frame; a remounting overlay reads it during render and seeds
// its springs from it, so the card reads as one object gliding across the
// navigation instead of blinking out and back. Route sections mount AppLayout
// *outside* Suspense, so a cross-section navigation swaps overlays in a single
// commit — the new overlay's render captures this value before the old overlay's
// unmount cleanup runs, which is why the write is continuous rather than on
// unmount. Unmount only *clears* it, and only once the tour has ended, so a later
// relaunch starts fresh; a full reload (which ends the tour) resets the module.
let carriedCardPos: { x: number; y: number } | null = null;

interface TourOverlayProps {
  step: TourStep;
  stepIndex: number;
  stepCount: number;
  targetEl: HTMLElement | null;
  isMobile: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

function readRect(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export function TourOverlay({
  step,
  stepIndex,
  stepCount,
  targetEl,
  isMobile,
  onNext,
  onBack,
  onSkip,
}: TourOverlayProps) {
  const reducedMotion = useReducedMotion();

  // Capture the cross-route carry on the first render of this mount (the spring
  // initial values below depend on it). The rAF loop keeps the module var current,
  // so whatever the previous overlay wrote on its last frame is here to read.
  const carryRef = useRef<{ x: number; y: number } | null | undefined>(undefined);
  if (carryRef.current === undefined) {
    carryRef.current = carriedCardPos;
  }
  const carry = carryRef.current;
  const skipEntrance = carry != null;

  // The committed presentation, driven by the loop through a change-gate once
  // the target size is stable (see STABLE_FRAMES): `true` = centered-large
  // (also the resolving/settled default), `false` = anchored-compact. Replaces
  // the v3.2 per-frame hugeness mirror, whose skeleton→content flip re-aimed
  // the card mid-glide.
  const [cardCentered, setCardCentered] = useState(true);
  const cardCenteredRef = useRef(true);

  // Every centered presentation (mobile, anchor-less step, resolving/settled
  // fallback, viewport-filling target) uses the large card and a full scrim; only
  // a size-stable, non-huge anchored target gets the compact card + a real hole.
  const centered = isMobile || step.anchor == null || cardCentered;
  const large = centered;

  // Spotlight-hole geometry: four springs, read every rAF frame to compose the
  // scrim path. Card position: two springs, bound to the card transform so framer
  // drives it. Card height: measured spring so content-size changes ease. All are
  // referentially stable across renders — the rAF loop closes over them safely.
  const hx = useSpring(0, HOLE_SPRING);
  const hy = useSpring(0, HOLE_SPRING);
  const hw = useSpring(0, HOLE_SPRING);
  const hh = useSpring(0, HOLE_SPRING);
  const initialX = carry?.x ?? Math.max(16, (window.innerWidth - LARGE_CARD_W) / 2);
  const initialY = carry?.y ?? Math.max(16, (window.innerHeight - 220) / 2);
  const cardX = useSpring(initialX, CARD_POS_SPRING);
  const cardY = useSpring(initialY, CARD_POS_SPRING);
  const cardHeight = useSpring(0, HEIGHT_SPRING);

  const pathRef = useRef<SVGPathElement>(null);
  const cardBoxRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  // Loop inputs that change across renders are read through refs so the single
  // rAF loop can run for the overlay's whole life without restarting (D4).
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;
  const targetElRef = useRef(targetEl);
  targetElRef.current = targetEl;
  const loopInputsRef = useRef({ isMobile, anchor: step.anchor, preferred: step.placement });
  loopInputsRef.current = { isMobile, anchor: step.anchor, preferred: step.placement };

  const prevSideRef = useRef<Side | null>(null);
  const holeOpenRef = useRef(false);
  const firstFrameRef = useRef(true);
  const lastDRef = useRef('');
  const stableSizeRef = useRef<{ w: number; h: number } | null>(null);
  const stableFramesRef = useRef(0);
  const adoptedRef = useRef(false);
  const lockedCenteredRef = useRef(true);
  const heightTargetRef = useRef(200);

  // Hysteresis, size-stability, and the one-shot placement commitment reset on
  // step change: a new step re-proves its target from scratch.
  useEffect(() => {
    prevSideRef.current = null;
    stableSizeRef.current = null;
    stableFramesRef.current = 0;
    adoptedRef.current = false;
  }, [stepIndex]);

  // Paint a full scrim on the very first paint (before the rAF loop runs) so
  // there is never a frame of undimmed page. Seed the idle-repaint guard too so
  // the first identical tick doesn't rewrite the attribute.
  useLayoutEffect(() => {
    const initial = composeHolePath(0, 0, 0, 0, window.innerWidth, window.innerHeight);
    lastDRef.current = initial;
    pathRef.current?.setAttribute('d', initial);
  }, []);

  // Measured-height spring (D6): observe the natural card height and drive the
  // size wrapper's animated height from it. First measure jumps (no open-animation
  // on mount); later measures spring, so step-to-step content-size changes ease.
  const [heightReady, setHeightReady] = useState(false);
  useEffect(() => {
    const el = measureRef.current;
    if (!el) return undefined;
    const apply = () => {
      const target = el.offsetHeight + CARD_BORDER_Y;
      heightTargetRef.current = target;
      if (!heightReady) {
        cardHeight.jump(target);
        setHeightReady(true);
      } else if (reducedMotionRef.current) {
        cardHeight.jump(target);
      } else {
        cardHeight.set(target);
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cardHeight, heightReady]);

  // Clear the carry only when the tour has ended (dismiss/complete), so a later
  // relaunch starts fresh. On a cross-route unmount the tour is still running, so
  // the value the rAF loop wrote last frame is left in place for the next mount.
  useEffect(() => {
    return () => {
      if (getTourState() == null) carriedCardPos = null;
    };
  }, []);

  // The single per-frame tracker (D4). Reads layout, pushes targets into springs,
  // writes the SVG `d` — the only setState it can reach is the change-gated
  // cardCentered mirror, which fires on a committed presentation change (at
  // target-size stability), never per frame.
  useEffect(() => {
    let raf = 0;

    const applyMV = (mv: MotionValue<number>, value: number) => {
      if (reducedMotionRef.current) mv.jump(value);
      else mv.set(value);
    };

    const tick = () => {
      const vp = { w: window.innerWidth, h: window.innerHeight };
      const inputs = loopInputsRef.current;
      const el = targetElRef.current;
      const connected = Boolean(el && el.isConnected);

      // F3 — Detached-target hold: targetEl is set but transiently off the DOM (a
      // refetch/skeleton element swap). Hold hole and card where they are and let
      // the provider's vanish-grace / timeout / a new element identity decide;
      // retargeting here would pulse the hole closed-and-reopened on every swap.
      const hold = Boolean(el && !connected && !inputs.isMobile && inputs.anchor != null);

      if (!hold) {
        const spotlight = Boolean(connected && !inputs.isMobile && inputs.anchor != null);

        // Size-stability tracking (see STABLE_FRAMES). Position changes (scroll)
        // don't reset the counter — only size does — so a stable card keeps
        // tracking an eased scroll without ever re-proving itself.
        let stable = false;
        let huge = false;
        let raw: Rect | null = null;
        if (spotlight && el) {
          raw = readRect(el);
          const last = stableSizeRef.current;
          if (
            last &&
            Math.abs(last.w - raw.width) <= SIZE_TOLERANCE &&
            Math.abs(last.h - raw.height) <= SIZE_TOLERANCE
          ) {
            stableFramesRef.current += 1;
          } else {
            stableSizeRef.current = { w: raw.width, h: raw.height };
            stableFramesRef.current = 0;
          }
          stable = stableFramesRef.current >= STABLE_FRAMES;
          // F2 — hugeness from the UNCLAMPED rect: scroll-invariant, growth-aware.
          huge = isHugeTarget(raw, vp);

          // One-shot commitment per step: a resting loading-skeleton passes the
          // size gate but is not the real content — its size lies about the
          // loaded page, and committing to it re-aims the card again when data
          // lands. Every Skeleton in this app pulses (`animate-pulse`), so the
          // card refuses to commit while the target still contains one. The
          // query runs only until adoption, never after. The mode LOCKS here:
          // later size churn may re-place the card along the fallback chain,
          // but can never flip its presentation again this step.
          if (!adoptedRef.current && stable && el.querySelector('.animate-pulse') == null) {
            adoptedRef.current = true;
            lockedCenteredRef.current = huge;
          }
        }

        // The committed presentation: centered-large until adoption, then the
        // locked mode. Mirrored to React through a change-gate (D4: the loop
        // never sets state per frame).
        const wantCentered = !spotlight || !adoptedRef.current || lockedCenteredRef.current;
        if (wantCentered !== cardCenteredRef.current) {
          cardCenteredRef.current = wantCentered;
          setCardCentered(wantCentered);
        }

        // Settled card size for all geometry — mode width constant + the height
        // spring's target — never the live, mid-animation offset size.
        const card = {
          w: wantCentered ? LARGE_CARD_W : COMPACT_CARD_W,
          h: heightTargetRef.current,
        };
        const center = {
          x: Math.max(VIEWPORT_MARGIN, (vp.w - card.w) / 2),
          y: Math.max(VIEWPORT_MARGIN, (vp.h - card.h) / 2),
        };

        // null = leave the card's current course untouched this frame (mid-load:
        // the hole tracks, the card commits once, when the target settles).
        let desired: { x: number; y: number } | null;

        if (spotlight && el && raw) {
          const padded = padRect(clampToViewport(raw, vp), SPOTLIGHT_PADDING);

          // Opening from a closed hole: jump to a zero-size rect at the new target's
          // center, then spring outward — reads as "refocus" rather than a slide
          // from the previous target's position (D3).
          if (!holeOpenRef.current) {
            hx.jump(padded.left + padded.width / 2);
            hy.jump(padded.top + padded.height / 2);
            hw.jump(0);
            hh.jump(0);
            holeOpenRef.current = true;
          }
          applyMV(hx, padded.left);
          applyMV(hy, padded.top);
          applyMV(hw, padded.width);
          applyMV(hh, padded.height);

          if (!adoptedRef.current || wantCentered) {
            // Pre-commitment (resolving/loading) and every centered presentation
            // share one constant attractor — no mid-load re-aiming.
            desired = center;
          } else if (!stable) {
            // Post-commitment size churn: hold the card's course; the hole tracks.
            desired = null;
          } else {
            const placement = computeCardPlacement(
              padded,
              card,
              inputs.preferred ?? 'bottom',
              prevSideRef.current,
              vp
            );
            if (placement.side !== 'center') prevSideRef.current = placement.side;
            desired = { x: placement.x, y: placement.y };
          }
        } else {
          // Closing: shrink the hole toward its current center, then let the path
          // composer drop the subpath once it falls below 1px (full scrim).
          if (holeOpenRef.current) {
            applyMV(hx, hx.get() + hw.get() / 2);
            applyMV(hy, hy.get() + hh.get() / 2);
            applyMV(hw, 0);
            applyMV(hh, 0);
            holeOpenRef.current = false;
          }
          desired = center;
        }

        if (desired) {
          if (firstFrameRef.current) {
            // Fresh start: jump so the entrance animation is a pure scale/fade at
            // the right spot. Carried across a route: glide from the carried
            // position.
            if (carryRef.current == null) {
              cardX.jump(desired.x);
              cardY.jump(desired.y);
            } else {
              cardX.set(desired.x);
              cardY.set(desired.y);
            }
          } else {
            applyMV(cardX, desired.x);
            applyMV(cardY, desired.y);
          }
        }
        firstFrameRef.current = false;
      }

      // F4 — Idle repaint guard: an identical `d` write still dirties the SVG and
      // repaints the full-viewport scrim, so skip it when nothing moved.
      const nextD = composeHolePath(hx.get(), hy.get(), hw.get(), hh.get(), vp.w, vp.h);
      if (nextD !== lastDRef.current) {
        lastDRef.current = nextD;
        pathRef.current?.setAttribute('d', nextD);
      }

      // F1 — Keep the cross-route carry current every frame (see the module var).
      carriedCardPos = { x: cardX.get(), y: cardY.get() };

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [hx, hy, hw, hh, cardX, cardY]);

  return (
    <>
      {/* Click shield: swallows all page interaction; only the card is live. */}
      <div
        className="fixed inset-0 z-[55]"
        aria-hidden
        onClickCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDownCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      {/* Scrim: one SVG, one even-odd path, `d` written imperatively per frame. */}
      <motion.svg
        className="pointer-events-none fixed inset-0 z-[60] h-full w-full"
        aria-hidden
        initial={skipEntrance || reducedMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      >
        <path ref={pathRef} fillRule="evenodd" className="fill-black/60" />
      </motion.svg>

      {/* Card: one persistent element. Position wrapper (spring transform) →
          entrance wrapper (tour-start scale/fade) → size wrapper (chrome, animated
          width/height) → measured content (chrome + crossfading step). */}
      <motion.div
        className="pointer-events-none fixed left-0 top-0 z-[70]"
        style={{ x: cardX, y: cardY }}
      >
        <motion.div
          initial={skipEntrance || reducedMotion ? false : { opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <motion.div
            ref={cardBoxRef}
            className={cn(
              'pointer-events-auto overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg',
              large ? 'w-[26rem] max-w-[calc(100vw-2rem)]' : 'w-80 max-w-[calc(100vw-2rem)]',
              !reducedMotion && 'transition-[width] duration-200'
            )}
            style={{ height: heightReady ? cardHeight : 'auto' }}
          >
            <div ref={measureRef}>
              <TourStepCard
                step={step}
                stepIndex={stepIndex}
                stepCount={stepCount}
                large={large}
                onNext={onNext}
                onBack={onBack}
                onSkip={onSkip}
              />
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </>
  );
}
